import moment from 'moment';

import {Avatar} from 'shells/avatar/avatar';
import type {TwoLineOptionWithIcon} from 'shells/select-two-line-icon/select-two-line-icon';
import type {Color} from 'shells/typography/body';

import type {
    GetActivityForModal_activity_participantsWithStatus as ParticipantEdge,
    GetActivityForModal_activity_participantsWithStatus_participant as Participant,
    ActivityParticipantAttendanceStatus,
} from 'nutshell-graphql-types';

import type {Option} from '../../../core/select/graphql-select-inline';

import {
    getTimeForSelectedDate as getTimeForSelectedDateShared,
    getValidEndTime as getValidEndTimeShared,
    getValidStartTime as getValidStartTimeShared,
} from '../../../../../core/date-time/activity-time-helpers';

import {AttendeeAddButton} from './attendee-add-button';
import type {ExternalAttendeeToContactData} from './participants-section';

/**
 * Moment-based wrapper around the shared helper for backwards compatibility.
 */
// @ts-expect-error
export const getTimeForSelectedDate = (date: Moment, time: Moment): Moment => {
    return moment.unix(getTimeForSelectedDateShared(date.unix(), time.unix()));
};

/**
 * Moment-based wrapper around the shared helper for backwards compatibility.
 */
export const getValidEndTime = (
    // @ts-expect-error
    newStartTime: Moment,
    // @ts-expect-error
    endTime: Moment,
    timeDuration: number
    // @ts-expect-error
): Moment => {
    return moment.unix(getValidEndTimeShared(newStartTime.unix(), endTime.unix(), timeDuration));
};

/**
 * Moment-based wrapper around the shared helper for backwards compatibility.
 */
// @ts-expect-error
export const getValidStartTime = (startTime: Moment, newEndTime: Moment): Moment => {
    return moment.unix(getValidStartTimeShared(startTime.unix(), newEndTime.unix()));
};

export const mapSelectOptionsToListOptions = (
    options: Option[],
    handleCreateContactFromExternalAttendee: (data: ExternalAttendeeToContactData) => void,
    isCreatingOrMergingContact: boolean,
    isReadOnly = false
): TwoLineOptionWithIcon<Option>[] => {
    return options.map((option) => {
        const href = option.metaData && option.metaData.href;
        const userEmail = option.type === 'users' ? stripHtml(option.relatedName || '') : null;
        const subLabel =
            option.emailAddress || stripHtml(option.primaryInfo || '') || userEmail || '';

        const actions =
            option.type === 'activityExternalAttendees' && !isReadOnly
                ? [
                      <AttendeeAddButton
                          key={`attendee-action-${option.id}`}
                          option={option}
                          handleCreateContactFromExternalAttendee={
                              handleCreateContactFromExternalAttendee
                          }
                          isCreatingOrMergingContact={isCreatingOrMergingContact}
                      />,
                  ]
                : [];

        return {
            label: option.name,
            value: option.id,
            sublabel: subLabel,
            iconNode: (
                <Avatar
                    type={option.type}
                    avatarUrl={option.avatarUrl}
                    initials={option.initials}
                    attendanceStatus={option.metaData?.attendanceStatus}
                    tooltipText={getAvatarTooltipText(
                        option.metaData?.attendanceStatus,
                        option.name
                    )}
                />
            ),
            href,
            metaData: {
                ...option,
                actions,
            },
            status: getStatus(option),
        };
    });
};

const getAvatarTooltipText = (
    attendanceStatus: ActivityParticipantAttendanceStatus | undefined,
    name: string
): string | undefined => {
    switch (attendanceStatus) {
        case 'ATTENDING':
            return `${name} has accepted the invitation`;
        case 'PENDING':
            return `${name} has not responded to the invitation`;
        case 'MAYBE':
            return `${name} has tentatively accepted the invitation`;
        case 'ABSENT':
            return `${name} has declined the invitation`;
        default:
            return;
    }
};

const statusMap: Record<
    ActivityParticipantAttendanceStatus,
    {text: string; color: Color} | undefined
> = {
    ATTENDING: {text: 'Attending', color: 'success'},
    PENDING: {text: 'Pending', color: 'medium'},
    MAYBE: {text: 'Maybe', color: 'orange-attention'},
    ABSENT: {text: 'Declined', color: 'error'},
};

const getStatus = (option: Option): {text: string; color: Color} | undefined => {
    const attendanceStatus = option.metaData?.attendanceStatus;
    if (!attendanceStatus) return undefined;

    return statusMap[attendanceStatus as ActivityParticipantAttendanceStatus];
};

export const mapParticipantEdgesToSelectOptions = (edges: ParticipantEdge[]): Option[] => {
    return edges.map((edge) => {
        const participant = edge.participant;

        return {
            id: participant.id,
            name: participant.name,
            initials: participant.initials || '',
            avatarUrl: participant.avatarUrl,
            emailAddress: participant.primaryEmailAddress || '',
            primaryInfo: participant.primaryPhoneNumber || '',
            type: participant.type,
            metaData: {
                href: participant.htmlUrl,
                attendanceStatus: edge.attendanceStatus,
            },
        };
    });
};

export const mapParticipantsToSelectOptions = (participants: Participant[]): Option[] => {
    return participants.map((participant) => {
        return {
            id: participant.id,
            name: participant.name,
            initials: participant.initials || '',
            avatarUrl: participant.avatarUrl,
            emailAddress: participant.primaryEmailAddress || '',
            primaryInfo: participant.primaryPhoneNumber || '',
            type: participant.type,
            metaData: {
                href: participant.htmlUrl,
            },
        };
    });
};

export const sortParticipantsOnAdd = (options: Option | Option[]): Option | Option[] => {
    if (!Array.isArray(options)) {
        return options;
    }

    return [...options].sort((a: Option, b: Option) => {
        if (a.type === 'activityExternalAttendees') {
            return 1;
        }
        if (b.type === 'activityExternalAttendees') {
            return -1;
        }

        return 0;
    });
};

export const mapListOptionsToSelectOptions = (
    options: TwoLineOptionWithIcon<Option>[]
): Option[] => {
    return options.map((option) => {
        const href = option.metaData?.metaData?.href;
        const attendanceStatus = option.metaData?.metaData?.attendanceStatus;

        return {
            id: option.value,
            name: option.label,
            initials: option.metaData?.initials || '',
            avatarUrl: option.metaData?.avatarUrl,
            emailAddress: option.metaData?.emailAddress || '',
            primaryInfo: option.metaData?.primaryInfo,
            type: option.metaData?.type,
            metaData: {
                href,
                attendanceStatus,
            },
        };
    });
};

export const stripHtml = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    return temp.textContent || temp.innerText || '';
};

// @ts-expect-error
export const getHasNonCreatorParticipants = (activity: Activity): boolean => {
    const creatorUser = activity.creatorUser;
    if (!creatorUser) {
        return false;
    }

    // @ts-expect-error
    return activity.participants.some((participant) => participant.name !== creatorUser.name);
};

// @ts-expect-error
export const getBannerSaveText = (activity: Activity): string => {
    // There are two cases where we consider this activity syncing for sake of
    // showing proper copy:
    // 1. The activity is already connected to an external calendar
    // 2. The activity is not yet connected to an external calendar, but it is
    //    set to sync to the calendar
    const isSyncing = activity.isConnectedToExternalCalendar || activity.shouldSyncToCalendar;
    const hasNonCreatorParticipants = getHasNonCreatorParticipants(activity);

    const isExternalSyncOrigin =
        activity.creatorOrigin &&
        (activity.creatorOrigin.originType === 'GOOGLE_API' ||
            activity.creatorOrigin.originType === 'OFFICE365_API');

    // When an activity is created in Nutshell, we dont add participants to the
    // external event. In the case of warning against notifying attendees, we
    // instead only need to check for activities where external participants may
    // be added, such as activities synced in from external sources or Scheduler
    const hasExternalAddedParticipants = isExternalSyncOrigin || activity.hasBooking;

    if (isSyncing && hasNonCreatorParticipants && hasExternalAddedParticipants) {
        return 'Changes made to this event may notify your attendees';
    }

    return 'Save your changes to this event';
};
